import { lazy } from "react";
import { Route, Navigate } from "react-router-dom";
import ProtectedRoute from "@components/shared/ProtectedRoute";
import ClientLayout from "@components/client/layouts/ClientLayout";
import AssetLayout from "@/layouts/AssetLayout";

// Lazy load client pages
const HomePage = lazy(() => import("@pages/client/Home"));
const HelpPage = lazy(() => import("@pages/client/Help"));
const HelpLayout = lazy(() => import("@pages/client/HelpLayout"));
const SingleHelp = lazy(() => import("@pages/client/SingleHelp"));
const GlossaryPage = lazy(() => import("@pages/client/Glossary"));
const GlossaryTermPage = lazy(() => import("@pages/client/GlossaryTerm"));
const ProfilePage = lazy(() => import("@pages/client/Profile"));
const ContactPage = lazy(() => import("@pages/client/Contact"));
const ContactsPage = lazy(() => import("@pages/client/Contacts"));
const SingleContact = lazy(() => import("@pages/client/SingleContact"));
const AccreditationPage = lazy(() => import("@pages/client/Accreditation"));
const SingleAccreditation = lazy(() => import("@pages/client/SingleAccreditation"));
const LibraryLayout = lazy(() => import("@pages/client/library/LibraryLayout"));
const LibraryCollection = lazy(() => import("@pages/client/LibraryCollection"));
const SingleMedia = lazy(() => import("@pages/client/SingleMedia"));
const Assets = lazy(() => import("@pages/client/Assets"));
const SingleAsset = lazy(() => import("@pages/client/SingleAsset"));
const NewsLayout = lazy(() => import("@pages/client/home/news/layout"));
const NewsList = lazy(() => import("@pages/client/home/news/NewsList"));
const NewsDetail = lazy(() => import("@pages/client/home/news/NewsDetail"));
const SingleApp = lazy(() => import("@pages/client/SingleApp"));
const Favourites = lazy(() => import("@pages/client/Favourites"));
const NotFoundPage = lazy(() => import("@pages/NotFound"));
const ForbiddenPage = lazy(() => import("@pages/Forbidden"));

/**
 * Client routes - at root level (no /client prefix)
 * All routes wrapped with ClientLayout for consistent UI and authentication
 *
 * Note: This returns JSX elements directly, not a component
 */
export const getClientRoutes = () => (
  <Route
    element={
      <ProtectedRoute requireAuth={true}>
        <ClientLayout />
      </ProtectedRoute>
    }
  >
    {/* Home */}
    <Route path="home" element={<HomePage />} />

    {/* News routes - nested under /news with layout */}
    <Route path="news" element={<NewsLayout />}>
      <Route index element={<Navigate to="all" replace />} />
      <Route path=":filter" element={<NewsList />} />
      <Route path=":filter/:newsId" element={<NewsDetail />} />
    </Route>

    {/* Help routes - list page without sidebar, detail page with sidebar */}
    <Route path="help">
      <Route index element={<HelpPage />} />
      <Route element={<HelpLayout />}>
        <Route path=":topicPath" element={<SingleHelp />} />
      </Route>
    </Route>

    {/* Glossary (ADR-0068) - search page and one term's page */}
    <Route path="glossary">
      <Route index element={<GlossaryPage />} />
      <Route path=":termId" element={<GlossaryTermPage />} />
    </Route>

    {/* Profile */}
    <Route path="profile" element={<ProfilePage />} />

    {/* Contact - tenant's own contact details page. A plain sibling route,
        not nested under /contacts, since that route's :contactId param
        would otherwise swallow this path. */}
    <Route path="contact" element={<ContactPage />} />

    {/* Contacts routes */}
    <Route path="contacts">
      <Route index element={<ContactsPage />} />
      <Route path=":contactId" element={<SingleContact />} />
    </Route>

    {/* Accreditation routes — detail views are owned here (not deep links into
        /library or /contacts) so every route this feature can reach is one
        an accreditor is entitled to, even when they have no access
        elsewhere in the tenant. */}
    <Route path="accreditation">
      <Route index element={<AccreditationPage />} />
      <Route path="media/:mediaType/:id" element={<SingleAccreditation />} />
      <Route path="contact/:id" element={<SingleAccreditation />} />
      <Route path="asset/:path" element={<SingleAccreditation />} />
    </Route>

    {/* Library routes with layout */}
    <Route path="library" element={<LibraryLayout />}>
      <Route path=":media/:filter" element={<LibraryCollection />} />
      <Route path=":media/:filter/:mediaId" element={<SingleMedia />} />
    </Route>

    {/* Asset routes */}
    <Route path="assets">
      <Route index element={<Navigate to="/home" replace />} />
      <Route path=":rootId" element={<Assets />} />
      <Route
        path=":rootId/:type/:path"
        element={
          <AssetLayout>
            <SingleAsset />
          </AssetLayout>
        }
      />
    </Route>

    {/* app routes */}
    <Route path="apps/:appId" element={<SingleApp />} />

    {/* Favourite routes */}
    <Route path="favourites" element={<Favourites />} />

    {/* Error pages — inside the layout so the top navbar stays. 403: a page
        or item the user may not see (ProtectedRoute role guards, detail-page
        403s); 404 and any unknown URL: not found. Signed out, the guard
        above sends them to login instead. */}
    <Route path="403" element={<ForbiddenPage />} />
    <Route path="404" element={<NotFoundPage />} />
    <Route path="*" element={<NotFoundPage />} />
  </Route>
);
